import * as ko from 'knockout';
import {ObjectTypeId, SPINNER} from '../../../../constants';
import {IMessageData, IObjectData} from '../../../../index';
import {IDialogOptions} from '../../../dialog/index';
import {ButtonManager} from '../../../../util/ButtonManager';
import {getCancelButton, getSubmitButton} from '../../../../constants/RouteDefinitions';
import PromiseRequest from '../../../../req/PromiseRequest';
import {KOSubscriptionTracker} from '../../../../util/KOSubscriptionTracker';
import {createErrorString, isPartOf} from '../../../../util/misc';
import {nsLanguage} from '../../../../util/nsLanguage';
import {global} from '../../../../appContext';
import {nsUserMembership} from '../nsUserMembership';
import runMembershipAddGroupsBatch = nsUserMembership.runMembershipAddGroupsBatch;
import IMembershipAddGroupFailure = nsUserMembership.IMembershipAddGroupFailure;
import add_groupsHtml from './add-groups.html?raw';

export const template = add_groupsHtml;

interface IParams {
    dialog: IDialogOptions;
    user: IObjectData;
    excludedGroupIds: number[];
    onComplete: () => void | Promise<void>;
}

enum Step {
    Pick,
    Process,
    Summary,
}

/** Wizard: pick groups, create add-membership overrides, then show a per-item result summary. */
export class viewModel {
    readonly Step = Step;
    private readonly kosub = new KOSubscriptionTracker();
    private readonly reqCreateOverride = new PromiseRequest('createoverride');
    private readonly onComplete: () => void | Promise<void>;
    private pendingGroups: IObjectData[] = [];

    readonly messageData = ko.observable<IMessageData>(null);
    readonly selectedObjects = ko.observableArray<IObjectData>([]);
    readonly pageButtons = new ButtonManager();
    readonly excludedGroupIds: number[];
    readonly user: IObjectData;
    readonly step = ko.observable<Step>(Step.Pick);
    readonly percentComplete = ko.observable<number>(null);
    readonly succeededGroups = ko.observableArray<IObjectData>([]);
    readonly failedGroups = ko.observableArray<IMembershipAddGroupFailure>([]);

    readonly isStep = isPartOf.bind(this, this.step);
    readonly createErrorString = createErrorString;

    constructor(private readonly params: IParams) {
        this.user = params.user;
        this.excludedGroupIds = params.excludedGroupIds;
        this.onComplete = params.onComplete;
        this.initButtons();
        this.kosub.subscribe(this.selectedObjects, () => this.updateSubmitButton());
        this.kosub.subscribe(this.step, step => this.onStepChange(step));
    }

    private initButtons(): void {
        this.pageButtons.add(getCancelButton(() => this.params.dialog.doClose?.(), {
            validator: () => this.isStep(Step.Pick),
        }));
        this.pageButtons.add(getSubmitButton({
            label: 'add',
            icon: 'fa-plus',
            onClick: () => this.onAddClick(),
            enabled: () => !!this.selectedObjects().length,
            validator: () => this.isStep(Step.Pick),
        }));
        this.pageButtons.add({
            onClick: () => this.params.dialog.doClose?.(),
            label: 'close',
            icon: 'fa-times',
            validator: () => this.isStep(Step.Summary),
        });
    }

    private updateSubmitButton(): void {
        this.pageButtons.buttons?.valueHasMutated?.();
    }

    private onAddClick(): void {
        const groups = this.selectedObjects();
        if (!groups.length || !this.isStep(Step.Pick)) {
            return;
        }
        this.pendingGroups = groups.slice();
        this.succeededGroups([]);
        this.failedGroups([]);
        this.step(Step.Process);
    }

    private onStepChange(step: Step): void {
        switch (step) {
            case Step.Process:
                this.params.dialog.isClosable?.(false);
                this.startProcessing();
                break;
            case Step.Summary:
                this.params.dialog.isClosable?.(true);
                this.finishSummary();
                break;
        }
    }

    private startProcessing(): void {
        const groups = this.pendingGroups;
        const total = groups.length;
        this.percentComplete(0);
        this.updateProcessMessage(0, total);
        runMembershipAddGroupsBatch(this.reqCreateOverride, this.user, groups, {
            onProgress: (processed, numTotal) => {
                this.percentComplete(numTotal ? processed / numTotal : null);
                this.updateProcessMessage(processed, numTotal);
            },
            onComplete: (successes, failures) => {
                this.succeededGroups(successes);
                this.failedGroups(failures);
                this.messageData(null);
                this.percentComplete(null);
                this.step(Step.Summary);
            },
        });
    }

    private updateProcessMessage(processed: number, total: number): void {
        this.messageData({message: `((creating|td)) [${processed}/${total}] ${SPINNER}`});
    }

    private finishSummary(): void {
        void Promise.resolve(this.onComplete());
        const n = this.succeededGroups().length;
        if (n) {
            global.snackbarData({message: this.getAddedSubjectMessage(n)});
        }
    }

    successSummaryMessage(): string {
        return this.getAddedSubjectMessage(this.succeededGroups().length);
    }

    private getAddedSubjectMessage(num: number): string {
        return `added subject|s:((${this.getNumGroupsLabel(num)}))`;
    }

    failureSummaryMessage(): string {
        const n = this.failedGroups().length;
        return `subject is not updated due to error|n:${n}|s:((${this.getNumGroupsLabel(n)}))`;
    }

    private getNumGroupsLabel(num: number): string {
        return nsLanguage.getNumObjectsLabel(num, ObjectTypeId.Group, false);
    }

    dispose(): void {
        this.kosub.unsuscribeAll();
        this.reqCreateOverride.abort();
        this.params.dialog.isClosable?.(true);
    }
}
